import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  SparklesIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { asistenteIaService } from '../services/api';

/**
 * Burbuja flotante con chat al asistente IA.
 * - Visible para usuarios autenticados con rol admin / contadora / jefe_operaciones.
 * - Mantiene el historial en `localStorage` para sobrevivir refreshes.
 * - Al iniciar sesión muestra un saludo personalizado con los pendientes que le tocan
 *   al rol (vacaciones por aprobar, permisos, reembolsos, etc.) y un badge con el
 *   contador encima de la burbuja.
 * - Envía cada mensaje al endpoint /api/asistente-ia/mensaje con el historial previo.
 */

const STORAGE_KEY = 'asistenteIa.historial';
const SALUDO_KEY = 'asistenteIa.saludo';
const MAX_HISTORIAL_LOCAL = 40; // pares user/model

/**
 * Render simple de markdown ligero:
 *   - **negritas** y *cursivas*
 *   - Listas con `- ` o `* ` al inicio de línea
 *   - Listas numeradas `1. `, `2. ` ...
 *   - Encabezados ## y ###
 *   - Saltos de línea preservados
 *   - Las "tablas" tipo `| A | B |` se filtran como párrafos normales (Gemini ya
 *     no debería emitirlas con el nuevo system prompt, pero por las dudas).
 */
function renderInline(texto, keyPrefix = '') {
  if (texto == null) return null;
  /* Procesa **bold** y *italic* en una sola pasada. */
  const partes = [];
  const regex = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|_[^_\n]+_)/g;
  let lastIdx = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(texto)) !== null) {
    if (m.index > lastIdx) partes.push(texto.slice(lastIdx, m.index));
    const tk = m[0];
    if (tk.startsWith('**')) {
      partes.push(
        <strong key={`${keyPrefix}-b-${i++}`} className="font-semibold text-slate-900">
          {tk.slice(2, -2)}
        </strong>
      );
    } else if (tk.startsWith('*') || tk.startsWith('_')) {
      partes.push(
        <em key={`${keyPrefix}-i-${i++}`} className="italic">
          {tk.slice(1, -1)}
        </em>
      );
    }
    lastIdx = m.index + tk.length;
  }
  if (lastIdx < texto.length) partes.push(texto.slice(lastIdx));
  return partes;
}

function renderTexto(texto) {
  if (!texto) return null;
  const lineas = String(texto).split('\n');
  const bloques = [];
  let listaActual = null; // { tipo: 'ul'|'ol', items: [] }

  const cerrarLista = () => {
    if (!listaActual) return;
    const Tag = listaActual.tipo === 'ol' ? 'ol' : 'ul';
    bloques.push(
      <Tag
        key={`l-${bloques.length}`}
        className={
          Tag === 'ol'
            ? 'list-decimal pl-5 my-1 space-y-0.5'
            : 'list-disc pl-5 my-1 space-y-0.5'
        }
      >
        {listaActual.items.map((it, idx) => (
          <li key={idx}>{renderInline(it, `li-${idx}`)}</li>
        ))}
      </Tag>
    );
    listaActual = null;
  };

  for (let idx = 0; idx < lineas.length; idx++) {
    const linea = lineas[idx];
    const recortada = linea.replace(/\s+$/, '');
    const limpio = recortada.replace(/^\s+/, '');

    // Saltar separadores de tabla tipo "|---|---|"
    if (/^[\s|:-]+$/.test(limpio) && /\|/.test(limpio)) continue;

    // Encabezado ###
    if (/^###\s+/.test(limpio)) {
      cerrarLista();
      bloques.push(
        <p key={`h3-${idx}`} className="font-semibold text-slate-800 mt-1">
          {renderInline(limpio.replace(/^###\s+/, ''))}
        </p>
      );
      continue;
    }
    if (/^##\s+/.test(limpio)) {
      cerrarLista();
      bloques.push(
        <p key={`h2-${idx}`} className="font-semibold text-slate-900 mt-1.5 text-[15px]">
          {renderInline(limpio.replace(/^##\s+/, ''))}
        </p>
      );
      continue;
    }

    // Lista no ordenada: "- texto" o "* texto"
    const mUl = /^[-*]\s+(.*)$/.exec(limpio);
    if (mUl) {
      if (!listaActual || listaActual.tipo !== 'ul') {
        cerrarLista();
        listaActual = { tipo: 'ul', items: [] };
      }
      listaActual.items.push(mUl[1]);
      continue;
    }

    // Lista ordenada: "1. texto", "2. texto"
    const mOl = /^\d+\.\s+(.*)$/.exec(limpio);
    if (mOl) {
      if (!listaActual || listaActual.tipo !== 'ol') {
        cerrarLista();
        listaActual = { tipo: 'ol', items: [] };
      }
      listaActual.items.push(mOl[1]);
      continue;
    }

    // Línea vacía → quiebre de párrafo
    if (limpio === '') {
      cerrarLista();
      bloques.push(<div key={`sp-${idx}`} className="h-1.5" />);
      continue;
    }

    // Tabla markdown horizontal "| col | col |" → la convertimos a línea simple
    if (/^\|.*\|$/.test(limpio)) {
      cerrarLista();
      const celdas = limpio
        .slice(1, -1)
        .split('|')
        .map((c) => c.trim())
        .filter(Boolean);
      bloques.push(
        <p key={`tr-${idx}`} className="my-0.5">
          {celdas.map((c, j) => (
            <React.Fragment key={j}>
              {j > 0 && <span className="text-slate-300 mx-1.5">·</span>}
              {renderInline(c, `tr-${idx}-${j}`)}
            </React.Fragment>
          ))}
        </p>
      );
      continue;
    }

    // Párrafo común
    cerrarLista();
    bloques.push(
      <p key={`p-${idx}`} className="my-0.5">
        {renderInline(limpio)}
      </p>
    );
  }
  cerrarLista();
  return bloques;
}

const SUGERENCIAS = [
  '¿Cuántos días le quedan a Rocío Picón?',
  'Lista los permisos pendientes',
  'Muéstrame los períodos de vacaciones de Nicolás Valdivia',
  'Resumen de permisos médicos de este mes'
];

export default function AsistenteIaBubble() {
  const { tieneRol, isAuthenticated, usuario } = useAuth();
  const habilitado =
    isAuthenticated && tieneRol('admin', 'contadora', 'jefe_operaciones');
  const [abierto, setAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [texto, setTexto] = useState('');
  const [historial, setHistorial] = useState([]);
  /* `saludo` se renderiza como una tarjeta fija arriba del historial. NO
   * forma parte del array `historial` enviado al modelo (es solo cliente). */
  const [saludo, setSaludo] = useState(null); // { total, saludo, categorias, token }
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          /* Migración de formato viejo (Gemini: `{ role: 'model', parts: [{text}] }`)
           * al nuevo (OpenAI: `{ role: 'assistant', content }`). */
          const migrado = parsed
            .map((m) => {
              if (m?.content && (m.role === 'user' || m.role === 'assistant')) return m;
              if (m?.role === 'user' && m.parts?.[0]?.text) {
                return { role: 'user', content: m.parts[0].text };
              }
              if (m?.role === 'model' && m.parts?.[0]?.text) {
                return { role: 'assistant', content: m.parts[0].text };
              }
              return null;
            })
            .filter(Boolean);
          setHistorial(migrado.slice(-MAX_HISTORIAL_LOCAL * 2));
        }
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(historial.slice(-MAX_HISTORIAL_LOCAL * 2)));
    } catch (_) {
      /* ignore quota errors */
    }
  }, [historial]);

  /* Saludo personalizado con pendientes:
   *   - Al montar (o cuando cambia el token = nueva sesión) consulta al backend
   *     el resumen de pendientes que le tocan al rol del usuario.
   *   - Si la sesión es la misma que ya teníamos cacheada (mismo token), usamos
   *     el saludo del localStorage para no martillar al backend en cada refresh.
   *   - Si es nueva sesión, se regenera y se persiste.
   *   - Cuando el usuario hace logout, se limpia. */
  useEffect(() => {
    if (!habilitado) {
      setSaludo(null);
      return;
    }
    const tokenActual = localStorage.getItem('token') || '';
    let cacheado = null;
    try {
      const raw = localStorage.getItem(SALUDO_KEY);
      if (raw) cacheado = JSON.parse(raw);
    } catch (_) {
      /* ignore */
    }

    /* Mismo token = misma sesión: mostramos el saludo cacheado y salimos. */
    if (cacheado && cacheado.token && cacheado.token === tokenActual) {
      setSaludo(cacheado);
      return;
    }

    /* Sesión nueva o sin cache: pedimos al backend. */
    let activo = true;
    asistenteIaService
      .pendientes()
      .then((res) => {
        if (!activo) return;
        const data = res.data?.data;
        if (!data) return;
        const payload = {
          total: Number(data.total || 0),
          saludo: String(data.saludo || ''),
          categorias: Array.isArray(data.categorias) ? data.categorias : [],
          token: tokenActual
        };
        setSaludo(payload);
        try {
          localStorage.setItem(SALUDO_KEY, JSON.stringify(payload));
        } catch (_) {
          /* ignore */
        }
      })
      .catch((err) => {
        /* Silencioso: si falla, simplemente no mostramos saludo. */
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[AsistenteIaBubble] No se pudo obtener pendientes:', err?.message);
        }
      });
    return () => {
      activo = false;
    };
  }, [habilitado, usuario?.id]);

  useEffect(() => {
    if (abierto && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [abierto, historial, enviando]);

  useEffect(() => {
    if (abierto && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [abierto]);

  const enviar = useCallback(
    async (mensajeOverride) => {
      const mensaje = (mensajeOverride ?? texto).trim();
      if (!mensaje || enviando) return;

      setTexto('');
      const historialPrevio = historial;
      /* Optimismo: mostramos el mensaje del usuario al instante. */
      setHistorial((prev) => [...prev, { role: 'user', content: mensaje }]);
      setEnviando(true);

      try {
        const res = await asistenteIaService.enviarMensaje(mensaje, historialPrevio);
        const nuevoHistorial = res.data?.data?.historial;
        if (Array.isArray(nuevoHistorial) && nuevoHistorial.length) {
          setHistorial(nuevoHistorial);
        } else if (res.data?.data?.respuesta) {
          setHistorial((prev) => [
            ...prev,
            { role: 'assistant', content: res.data.data.respuesta }
          ]);
        }
      } catch (err) {
        const msg = err.response?.data?.mensaje || 'No pude enviar el mensaje. Intentá de nuevo.';
        toast.error(msg);
        setHistorial((prev) => [...prev, { role: 'assistant', content: `_${msg}_` }]);
      } finally {
        setEnviando(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    },
    [texto, enviando, historial]
  );

  const limpiarConversacion = () => {
    if (!window.confirm('¿Borrar toda la conversación? Esta acción no se puede deshacer.')) return;
    setHistorial([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  if (!habilitado) return null;

  return (
    <>
      {!abierto && (
        <button
          type="button"
          onClick={() => setAbierto(true)}
          aria-label={
            saludo?.total > 0
              ? `Abrir asistente IA - ${saludo.total} pendientes`
              : 'Abrir asistente IA'
          }
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-2 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 px-4 py-3 text-white shadow-lg shadow-teal-500/30 hover:scale-105 hover:shadow-xl transition-all"
        >
          <SparklesIcon className="w-6 h-6" />
          <span className="hidden md:inline font-medium text-sm pr-1">Asistente IA</span>
          {saludo?.total > 0 ? (
            <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold shadow ring-2 ring-white">
              {saludo.total > 99 ? '99+' : saludo.total}
            </span>
          ) : (
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-400"></span>
            </span>
          )}
        </button>
      )}

      {abierto && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-3rem))] bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm leading-tight truncate">Asistente Prayaga</div>
                <div className="text-[11px] opacity-80 leading-tight">
                  Solo consultas · {usuario?.rol_nombre || 'usuario'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {historial.length > 0 && (
                <button
                  type="button"
                  onClick={limpiarConversacion}
                  className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                  aria-label="Borrar conversación"
                  title="Borrar conversación"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setAbierto(false)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Cerrar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50 space-y-3">
            {saludo && (
              <div
                className={`rounded-2xl border px-3.5 py-3 text-sm shadow-sm ${
                  saludo.total > 0
                    ? 'border-amber-200 bg-amber-50 text-amber-900'
                    : 'border-teal-200 bg-teal-50 text-teal-900'
                }`}
              >
                <div className="flex items-start gap-2">
                  <SparklesIcon
                    className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      saludo.total > 0 ? 'text-amber-600' : 'text-teal-600'
                    }`}
                  />
                  <div className="min-w-0 flex-1 leading-relaxed">
                    {renderTexto(saludo.saludo)}
                  </div>
                </div>
              </div>
            )}

            {historial.length === 0 && (
              <div className="text-center py-6">
                <div className="inline-flex w-14 h-14 rounded-full bg-gradient-to-br from-teal-100 to-cyan-100 items-center justify-center mb-3">
                  <SparklesIcon className="w-7 h-7 text-teal-600" />
                </div>
                <h3 className="text-slate-800 font-semibold text-sm mb-1">
                  {saludo ? '¿En qué te ayudo?' : `¡Hola${usuario?.nombres ? `, ${usuario.nombres.split(' ')[0]}` : ''}!`}
                </h3>
                <p className="text-slate-500 text-xs mb-4 px-4">
                  Puedo ayudarte a consultar vacaciones, permisos y datos de empleados.
                  Pediré confirmación antes de cualquier cambio futuro.
                </p>
                <div className="space-y-2 px-2">
                  {SUGERENCIAS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => enviar(s)}
                      disabled={enviando}
                      className="block w-full text-left text-xs text-slate-700 bg-white border border-slate-200 rounded-xl px-3 py-2 hover:border-teal-400 hover:bg-teal-50 transition-colors disabled:opacity-50"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {historial.map((m, i) => {
              const esUsuario = m.role === 'user';
              /* Compat: aceptamos tanto `content` (formato nuevo OpenAI) como
               * `parts[0].text` (formato Gemini, por si quedaron mensajes viejos
               * en localStorage de la versión anterior). */
              const contenido = m.content ?? m.parts?.[0]?.text ?? '';
              return (
                <div
                  key={i}
                  className={`flex ${esUsuario ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed break-words ${
                      esUsuario
                        ? 'bg-teal-600 text-white rounded-br-sm whitespace-pre-wrap'
                        : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {renderTexto(contenido)}
                  </div>
                </div>
              );
            })}

            {enviando && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2 shadow-sm flex items-center gap-2 text-slate-500 text-sm">
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  <span>Pensando...</span>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
            className="p-3 border-t border-slate-200 bg-white"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    enviar();
                  }
                }}
                placeholder="Escribí tu consulta..."
                rows={1}
                disabled={enviando}
                className="flex-1 resize-none rounded-2xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 max-h-32 overflow-y-auto disabled:bg-slate-50"
                style={{ minHeight: 38 }}
              />
              <button
                type="submit"
                disabled={enviando || !texto.trim()}
                aria-label="Enviar"
                className="rounded-full w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 transition-transform"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              Las respuestas pueden no ser perfectas. Verificá información sensible.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
